// node module
import { useCallback, useEffect, useRef } from 'react';
import { useThemeDetector } from '@upstox/upstox-ui';
// modules
import Routing from 'modules/Routing';
import AppService from 'modules/App/service';
import AnalyticsService from 'modules/Analytics';
import { useStore } from 'modules/Stores';
import { ReleaseNotesIndicatorStates } from 'modules/App';
// constants
import { EVENTS } from 'modules/Analytics/consts';
import { pulsatingDotPurpleLottieOptions, pulsatingDotRedLottieOptions } from 'utils/hooks/constants/webLottieConstants';
// hooks
import useLazyLottie from 'utils/hooks/useLazyLottie';
import { updateWhatsNewIndicatorStatusInLS } from 'utils/helpers/whatsNewUtils';

export const useCustomDOMWidgetButton = (
  customDOMIcon: string,
  releaseNotesIcon: (isFirstTime: boolean) => string,
  communityIcon: string,
) => {
  const rightToolbarDOMButtonTimeout =
    useRef<ReturnType<typeof setTimeout> | null>();
  const rightToolbarDOMButton = useRef<HTMLElement | null>(null);
  const rightToolbarReleaseNotesButtonContainer =
    useRef<HTMLDivElement | null>(null);
  const rightToolbarReleaseNotesButton = useRef<HTMLElement | null>(null);
  const rightToolbarCommunityButton = useRef<HTMLElement | null>(null);
  const { theme } = useStore('AppStore');
  const autoTheme = useThemeDetector();
  const currentTheme = theme === 'auto' ? autoTheme : theme;
  const isDarkTheme = currentTheme === 'dark';
  const { containerRef: purplePulsatingLottieRef } =
    useLazyLottie(pulsatingDotPurpleLottieOptions(isDarkTheme));
  const { containerRef: redPulsatingLottieRef } =
    useLazyLottie(pulsatingDotRedLottieOptions(isDarkTheme));

  const { toggleModal, releaseNotesIndicatorState, setReleaseNotesIndicatorState } = useStore('AppStore');

  const handleRightToolbarDOMClick = () => {
    const tvIframe = document.querySelector('iframe');
    // Create a new keyboard event to open the DOM widget
    const KEY_DOWN_EVENT_TO_OPEN_DOM = new KeyboardEvent('keydown', {
      key: 'D',
      code: 'KeyD',
      keyCode: 68, // 'D' key code
      charCode: 0,
      shiftKey: true, // Indicates the Shift key is held down
      bubbles: true, // Event will bubble up through the DOM
      cancelable: true, // Event can be canceled
    });

    if (tvIframe?.contentDocument) {
      // Dispatch the event on a target element
      tvIframe.contentDocument.dispatchEvent(KEY_DOWN_EVENT_TO_OPEN_DOM);
    }
  };

  const sendAnalyticsEvent = (icon: string) => {
    const eventData = {
      'Icon Name': icon,
    };
    AnalyticsService.sendEvent(EVENTS.ICON_CLICKED, eventData, true);
  };

  const showPulsatingLottieIfNeeded = useCallback(() => {
    purplePulsatingLottieRef.current = null;
    redPulsatingLottieRef.current = null;
    if (!rightToolbarReleaseNotesButtonContainer.current) {
      return;
    }
    if (
      releaseNotesIndicatorState === ReleaseNotesIndicatorStates.NEW_INTIMATION
    ) {
      purplePulsatingLottieRef.current =
        rightToolbarReleaseNotesButtonContainer.current.appendChild(
          document.createElement('div'),
        );
      purplePulsatingLottieRef.current.setAttribute(
        'style',
        'position: absolute; right: -1px; top: -4px; z-index: 2; width: 20px; height: 20px;',
      );
    } else if (
      releaseNotesIndicatorState === ReleaseNotesIndicatorStates.INTIMATION ||
      releaseNotesIndicatorState ===
        ReleaseNotesIndicatorStates.NEW_RELEASE_NOTES
    ) {
      redPulsatingLottieRef.current =
        rightToolbarReleaseNotesButtonContainer.current.appendChild(
          document.createElement('div'),
        );
      redPulsatingLottieRef.current.setAttribute(
        'style',
        'position: absolute; right: -1px; top: -4px; z-index: 2; width: 20px; height: 20px;',
      );
    }
  }, [
    purplePulsatingLottieRef,
    redPulsatingLottieRef,
    releaseNotesIndicatorState,
  ]);

  const handleRightToolbarReleaseNotesClick = useCallback(() => {
    // Check if the state indicates a new or pending intimation
    const isIntimation = releaseNotesIndicatorState ===
      ReleaseNotesIndicatorStates.NEW_INTIMATION ||
      releaseNotesIndicatorState === ReleaseNotesIndicatorStates.INTIMATION;

    // Check if the state indicates new release notes that haven't been seen yet
    const isNewReleaseNotes = releaseNotesIndicatorState ===
      ReleaseNotesIndicatorStates.NEW_RELEASE_NOTES;

    const isReleaseNotes = releaseNotesIndicatorState ===
      ReleaseNotesIndicatorStates.RELEASE_NOTES;

    if (isIntimation) {
      // Show chart sync disclaimer modal for intimation-related states
      toggleModal('chartSyncDisclaimer');
    } else if (isNewReleaseNotes) {
      // Update state as seen in local storage and open the release notes modal
      setReleaseNotesIndicatorState(ReleaseNotesIndicatorStates.RELEASE_NOTES);
      updateWhatsNewIndicatorStatusInLS();
      toggleModal('releaseNotes');
    } else if (isReleaseNotes) {
      // Simply open the release notes modal
      toggleModal('releaseNotes');
    }

    sendAnalyticsEvent("What's New");
  }, [releaseNotesIndicatorState, toggleModal, setReleaseNotesIndicatorState]);

  const handleRightToolbarCommunityClick = useCallback(() => {
    Routing.openNewUrl(AppService.config.releaseNotes.communityUrl);
    sendAnalyticsEvent('Community');
  }, []);

  const createRightToolbarBarDOMButton = useCallback(() => {
    const rightToolbar = document
      .querySelector('iframe')
      ?.contentDocument?.querySelectorAll('[data-name="right-toolbar"]')[0];

    if (rightToolbar) {
      rightToolbarDOMButton.current = rightToolbar.appendChild(
        document.createElement('button'),
      );
      rightToolbarDOMButton.current.innerHTML = customDOMIcon;
      rightToolbarDOMButton.current.setAttribute('aria-label', 'DOM');
      rightToolbarDOMButton.current.setAttribute('type', 'button');
      rightToolbarDOMButton.current.setAttribute('data-tooltip', 'DOM');
      rightToolbarDOMButton.current.setAttribute(
        'class',
        'custom-dom-button apply-common-tooltip common-tooltip-vertical',
      );
      rightToolbarDOMButton.current.addEventListener(
        'click',
        handleRightToolbarDOMClick,
      );

      const separatorContainer = rightToolbar.appendChild(
        document.createElement('div'),
      );
      separatorContainer.setAttribute(
        'class',
        'right-toolbar-separator-container',
      );

      const separator = separatorContainer.appendChild(
        document.createElement('div'),
      );
      separator.setAttribute('class', 'separator');

      rightToolbarReleaseNotesButtonContainer.current =
        rightToolbar.appendChild(
          document.createElement('div'),
        );
      rightToolbarReleaseNotesButtonContainer.current.setAttribute(
        'style',
        'position: relative;',
      );

      showPulsatingLottieIfNeeded();

      rightToolbarReleaseNotesButton.current =
        rightToolbarReleaseNotesButtonContainer.current.appendChild(
          document.createElement('button'),
        );
      rightToolbarReleaseNotesButton.current.innerHTML = releaseNotesIcon(
        releaseNotesIndicatorState ===
          ReleaseNotesIndicatorStates.NEW_INTIMATION,
      );
      rightToolbarReleaseNotesButton.current.setAttribute(
        'aria-label',
        "What's New",
      );
      rightToolbarReleaseNotesButton.current.setAttribute('type', 'button');
      rightToolbarReleaseNotesButton.current.setAttribute(
        'data-tooltip',
        "What's New",
      );
      rightToolbarReleaseNotesButton.current.setAttribute(
        'class',
        'custom-release-notes-button apply-common-tooltip common-tooltip-vertical',
      );
      rightToolbarReleaseNotesButton.current.addEventListener(
        'click',
        handleRightToolbarReleaseNotesClick,
      );

      rightToolbarCommunityButton.current = rightToolbar.appendChild(
        document.createElement('button'),
      );
      rightToolbarCommunityButton.current.innerHTML = communityIcon;
      rightToolbarCommunityButton.current.setAttribute(
        'aria-label',
        'Community',
      );
      rightToolbarCommunityButton.current.setAttribute('type', 'button');
      rightToolbarCommunityButton.current.setAttribute(
        'data-tooltip',
        'Community',
      );
      rightToolbarCommunityButton.current.setAttribute(
        'class',
        'custom-release-notes-button apply-common-tooltip common-tooltip-vertical',
      );
      rightToolbarCommunityButton.current.addEventListener(
        'click',
        handleRightToolbarCommunityClick,
      );
    }
  }, [
    communityIcon,
    customDOMIcon,
    handleRightToolbarCommunityClick,
    handleRightToolbarReleaseNotesClick,
    releaseNotesIcon,
    releaseNotesIndicatorState,
    showPulsatingLottieIfNeeded,
  ]);

  const handleRightToolbarDOMButton = useCallback(() => {
    if (rightToolbarDOMButtonTimeout?.current) {
      clearTimeout(rightToolbarDOMButtonTimeout?.current);
    }
    rightToolbarDOMButtonTimeout.current = setTimeout(() => {
      createRightToolbarBarDOMButton();
    }, 50);
  }, [createRightToolbarBarDOMButton]);

  useEffect(() => {
    if (rightToolbarReleaseNotesButton.current) {
      // Add an event listener for handling release notes button clicks
      // This ensures that the latest function reference is used after state changes
      rightToolbarReleaseNotesButton.current.addEventListener('click', handleRightToolbarReleaseNotesClick);
      // Update the button's inner HTML based on the release notes indicator state
      rightToolbarReleaseNotesButton.current.innerHTML = releaseNotesIcon(
        releaseNotesIndicatorState ===
          ReleaseNotesIndicatorStates.NEW_INTIMATION,
      );
      // Update pulsating Lottie animation based on the release notes indicator state
      showPulsatingLottieIfNeeded();
    }
    // Cleanup function to remove event listeners when the component unmounts
    return () => {
      if (rightToolbarDOMButton.current) {
        rightToolbarDOMButton.current.removeEventListener('click', handleRightToolbarDOMClick);
      }
      if (rightToolbarReleaseNotesButton.current) {
        rightToolbarReleaseNotesButton.current.removeEventListener(
          'click',
          handleRightToolbarReleaseNotesClick,
        );
      }
    };
  }, [
    releaseNotesIndicatorState,
    handleRightToolbarReleaseNotesClick,
    showPulsatingLottieIfNeeded,
    releaseNotesIcon,
  ]);

  return {
    rightToolbarDOMButton,
    rightToolbarReleaseNotesButton,
    rightToolbarCommunityButton,
    handleRightToolbarDOMButton,
  };
};

export default useCustomDOMWidgetButton;
